import { supabase } from '../lib/supabase';
import type { PlayerAttribute } from '../types/playerCard';
import { getAttributeDefinitions } from '../types/playerCard';

/**
 * Service for managing Player Attributes
 * Handles sport-specific attribute management (physical, technical, mental)
 */
class PlayerAttributeService {
  /**
   * Get all attributes for a player card and sport
   */
  async getAttributes(
    cardId: string,
    sport: string
  ): Promise<PlayerAttribute[]> {
    const { data, error } = await supabase
      .from('player_attributes')
      .select('*')
      .eq('player_card_id', cardId)
      .eq('sport', sport)
      .order('category', { ascending: true })
      .order('attribute_key', { ascending: true });

    if (error) {
      console.error('[PlayerAttributeService] Error fetching attributes:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Get a specific attribute
   */
  async getAttribute(
    cardId: string,
    sport: string,
    category: 'physical' | 'technical' | 'mental',
    attributeKey: string
  ): Promise<PlayerAttribute | null> {
    const { data, error } = await supabase
      .from('player_attributes')
      .select('*')
      .eq('player_card_id', cardId)
      .eq('sport', sport)
      .eq('category', category)
      .eq('attribute_key', attributeKey)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Not found
      }
      console.error('[PlayerAttributeService] Error fetching attribute:', error);
      throw error;
    }

    return data;
  }

  /**
   * Create or update an attribute
   * Uses upsert to handle both create and update
   */
  async upsertAttribute(
    cardId: string,
    sport: string,
    category: 'physical' | 'technical' | 'mental',
    attributeKey: string,
    attributeName: string,
    value: number,
    notes?: string
  ): Promise<PlayerAttribute> {
    // Validate value range
    if (value < 1 || value > 10) {
      throw new Error('Attribute value must be between 1 and 10');
    }

    const { data, error } = await supabase
      .from('player_attributes')
      .upsert({
        player_card_id: cardId,
        sport,
        category,
        attribute_key: attributeKey,
        attribute_name: attributeName,
        value: Math.round(value), // Ensure integer
        notes: notes || null,
        updated_at: new Date().toISOString(),
      }, {
        onConflict: 'player_card_id,sport,category,attribute_key',
      })
      .select()
      .single();

    if (error) {
      console.error('[PlayerAttributeService] Error upserting attribute:', error);
      throw error;
    }

    return data;
  }

  /**
   * Update an existing attribute value
   */
  async updateAttribute(
    cardId: string,
    sport: string,
    category: 'physical' | 'technical' | 'mental',
    attributeKey: string,
    value: number,
    notes?: string
  ): Promise<PlayerAttribute> {
    // Validate value range
    if (value < 1 || value > 10) {
      throw new Error('Attribute value must be between 1 and 10');
    }

    const { data, error } = await supabase
      .from('player_attributes')
      .update({
        value: Math.round(value),
        notes: notes !== undefined ? notes : undefined,
        updated_at: new Date().toISOString(),
      })
      .eq('player_card_id', cardId)
      .eq('sport', sport)
      .eq('category', category)
      .eq('attribute_key', attributeKey)
      .select()
      .single();

    if (error) {
      console.error('[PlayerAttributeService] Error updating attribute:', error);
      throw error;
    }

    return data;
  }

  /**
   * Delete an attribute
   */
  async deleteAttribute(
    cardId: string,
    sport: string,
    category: 'physical' | 'technical' | 'mental',
    attributeKey: string
  ): Promise<void> {
    const { error } = await supabase
      .from('player_attributes')
      .delete()
      .eq('player_card_id', cardId)
      .eq('sport', sport)
      .eq('category', category)
      .eq('attribute_key', attributeKey);

    if (error) {
      console.error('[PlayerAttributeService] Error deleting attribute:', error);
      throw error;
    }
  }

  /**
   * Initialize default attributes for a player card and sport
   * Creates all required attributes with default value of 5
   * For football, only creates required technical attributes (optional ones are added on demand)
   */
  async initializeDefaultAttributes(
    cardId: string,
    sport: string
  ): Promise<PlayerAttribute[]> {
    const definitions = getAttributeDefinitions(sport);
    const attributes: PlayerAttribute[] = [];

    // Create physical attributes (all required)
    for (const key of definitions.physical) {
      const attr = await this.upsertAttribute(
        cardId,
        sport,
        'physical',
        key,
        this.formatAttributeName(key),
        5 // Default value
      );
      attributes.push(attr);
    }

    // Create technical attributes
    // For sports with required/optional structure (Football, Baseball, Softball), only create required ones initially
    if (definitions.technicalRequired) {
      // Only create required technical attributes
      for (const key of definitions.technicalRequired) {
        const attr = await this.upsertAttribute(
          cardId,
          sport,
          'technical',
          key,
          this.formatAttributeName(key),
          5 // Default value
        );
        attributes.push(attr);
      }
    } else {
      // For other sports, create all technical attributes
      for (const key of definitions.technical) {
        const attr = await this.upsertAttribute(
          cardId,
          sport,
          'technical',
          key,
          this.formatAttributeName(key),
          5 // Default value
        );
        attributes.push(attr);
      }
    }

    // Create mental attributes (all required)
    for (const key of definitions.mental) {
      const attr = await this.upsertAttribute(
        cardId,
        sport,
        'mental',
        key,
        this.formatAttributeName(key),
        5 // Default value
      );
      attributes.push(attr);
    }

    return attributes;
  }

  /**
   * Add an optional technical attribute for a player card
   * Useful for position-specific attributes like snapping, throwing_power (Football),
   * or pitching, catching (Baseball/Softball)
   */
  async addOptionalTechnicalAttribute(
    cardId: string,
    sport: string,
    attributeKey: string,
    initialValue: number = 5
  ): Promise<PlayerAttribute> {
    return this.upsertAttribute(
      cardId,
      sport,
      'technical',
      attributeKey,
      this.formatAttributeName(attributeKey),
      initialValue
    );
  }

  /**
   * Batch update multiple attributes
   */
  async batchUpdateAttributes(
    cardId: string,
    sport: string,
    updates: Array<{
      category: 'physical' | 'technical' | 'mental';
      attributeKey: string;
      value: number;
      notes?: string;
    }>
  ): Promise<PlayerAttribute[]> {
    const results: PlayerAttribute[] = [];

    for (const update of updates) {
      const attr = await this.upsertAttribute(
        cardId,
        sport,
        update.category,
        update.attributeKey,
        this.formatAttributeName(update.attributeKey),
        update.value,
        update.notes
      );
      results.push(attr);
    }

    return results;
  }

  /**
   * Format attribute key to display name
   */
  private formatAttributeName(key: string): string {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Get attribute definitions for a sport
   */
  getAttributeDefinitions(sport: string) {
    return getAttributeDefinitions(sport);
  }
}

export const playerAttributeService = new PlayerAttributeService();

